import { useMemo, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { nanoToUsdCompact } from '../../../lib/format.js';
import type { PivotRow } from '../../../api/admin.js';

const COL_TOTAL = '∑';
const NULL_KEY = '∅';

interface PivotTableProps {
  rows: ReadonlyArray<PivotRow>;
  rowLabel: string;
  colLabel: string | null;
  /**
   * Si el eje de filas es temporal (day/week/month), ordena cronológicamente
   * (asc por clave) en vez de por total descendente.
   */
  temporalRow?: boolean;
  /** Devuelve un href de drill-down para una celda (o null si no aplica). */
  drillHref?: ((rowKey: string | null, colKey: string | null) => string | null) | undefined;
}

/**
 * Orden canónico de las claves de fila, compartido con el chart de la página
 * para que tabla y gráfico siempre coincidan: cronológico si es temporal,
 * por total descendente si no.
 */
export function orderRowKeys(
  keys: Iterable<string>,
  totals: ReadonlyMap<string, bigint>,
  temporal: boolean,
): string[] {
  const arr = [...keys];
  if (temporal) {
    return arr.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  }
  return arr.sort((a, b) => Number((totals.get(b) ?? 0n) - (totals.get(a) ?? 0n)));
}

/**
 * Reshape de filas planas `{rowKey,colKey,costNano}` a una matriz row × col
 * con totales por fila/columna. Las celdas con `drillHref` no-null linkean al
 * detalle (típicamente /sessions filtrado).
 */
export function PivotTable({
  rows,
  rowLabel,
  colLabel,
  temporalRow = false,
  drillHref,
}: PivotTableProps): ReactElement {
  const { rowKeys, colKeys, matrix, rowTotals, colTotals, grandTotal } = useMemo(() => {
    const rowSet = new Set<string>();
    const colSet = new Set<string>();
    const m = new Map<string, bigint>();
    const rTot = new Map<string, bigint>();
    const cTot = new Map<string, bigint>();
    let grand = 0n;
    for (const r of rows) {
      const rk = r.rowKey ?? NULL_KEY;
      const ck = r.colKey ?? NULL_KEY;
      const cost = BigInt(r.costNano);
      rowSet.add(rk);
      colSet.add(ck);
      m.set(`${rk} ${ck}`, (m.get(`${rk} ${ck}`) ?? 0n) + cost);
      rTot.set(rk, (rTot.get(rk) ?? 0n) + cost);
      cTot.set(ck, (cTot.get(ck) ?? 0n) + cost);
      grand += cost;
    }
    // Filas: cronológico si temporal, por total desc si no. Columnas: alfabético.
    const rowKeysArr = orderRowKeys(rowSet, rTot, temporalRow);
    const colKeysArr = [...colSet].sort();
    return {
      rowKeys: rowKeysArr,
      colKeys: colKeysArr,
      matrix: m,
      rowTotals: rTot,
      colTotals: cTot,
      grandTotal: grand,
    };
  }, [rows, temporalRow]);

  const hasCols = colLabel !== null && (colKeys.length > 1 || (colKeys[0] ?? NULL_KEY) !== NULL_KEY);

  if (rows.length === 0) {
    return <div className="empty">Sin datos en el rango / filtros seleccionados.</div>;
  }

  function cellContent(rk: string, ck: string): ReactElement {
    const v = matrix.get(`${rk} ${ck}`) ?? 0n;
    const text = v === 0n ? <span className="muted">—</span> : nanoToUsdCompact(v.toString());
    const href = drillHref?.(rk === NULL_KEY ? null : rk, ck === NULL_KEY ? null : ck) ?? null;
    if (href && v > 0n) {
      return (
        <Link to={href} className="mono">
          {text}
        </Link>
      );
    }
    return <span className="mono">{text}</span>;
  }

  return (
    <table className="table" data-testid="pivot-table">
      <thead>
        <tr>
          <th>{rowLabel}</th>
          {hasCols ? colKeys.map((ck) => <th key={ck} className="r">{ck}</th>) : <th className="r">cost</th>}
          {hasCols ? <th className="r">{COL_TOTAL}</th> : null}
        </tr>
      </thead>
      <tbody>
        {rowKeys.map((rk) => (
          <tr key={rk}>
            <td className="mono tt-sec">{rk}</td>
            {hasCols ? (
              colKeys.map((ck) => (
                <td key={ck} className="r">
                  {cellContent(rk, ck)}
                </td>
              ))
            ) : (
              <td className="r">{cellContent(rk, colKeys[0] ?? NULL_KEY)}</td>
            )}
            {hasCols ? (
              <td className="r mono" style={{ fontWeight: 600 }}>
                {nanoToUsdCompact((rowTotals.get(rk) ?? 0n).toString())}
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td className="mono" style={{ fontWeight: 600 }}>
            {COL_TOTAL}
          </td>
          {hasCols ? (
            colKeys.map((ck) => (
              <td key={ck} className="r mono" style={{ fontWeight: 600 }}>
                {nanoToUsdCompact((colTotals.get(ck) ?? 0n).toString())}
              </td>
            ))
          ) : (
            <td className="r mono" style={{ fontWeight: 600 }}>
              {nanoToUsdCompact(grandTotal.toString())}
            </td>
          )}
          {hasCols ? (
            <td className="r mono" style={{ fontWeight: 700 }}>
              {nanoToUsdCompact(grandTotal.toString())}
            </td>
          ) : null}
        </tr>
      </tfoot>
    </table>
  );
}
